// components/collection/CollectionItem.js
import React, { useState } from 'react';
import { Container, Card, Button, Badge, Modal } from 'react-bootstrap';
import { InfoCircle, FileText, Pencil, X, Newspaper, List, ShieldCheck, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

// Optional Capacitor import for native app functionality
let Browser, Haptics, ImpactStyle;
try {
  Browser = require('@capacitor/browser').Browser;
  const haptics = require('@capacitor/haptics');
  Haptics = haptics.Haptics;
  ImpactStyle = haptics.ImpactStyle;
} catch (error) {
  Browser = null;
  Haptics = null;
  ImpactStyle = null;
}

// ============================================
// Utility Functions (unchanged)
// ============================================

const formatDate = (inputDate) => {
  if (!inputDate) return '';
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }
  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', inputDate);
      return '';
    }
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const day = utcDate.getDate();
    const month = utcDate.getMonth() + 1;
    const year = utcDate.getFullYear();
    return `${month}-${day}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const safeDisplay = (value) => {
  return value && value !== 'null' && value !== null ? value : '';
};

const buildMetadataString = (item) => {
  const parts = [
    safeDisplay(item.feedName),
    safeDisplay(item.branch),
    safeDisplay(item.topic)
  ].filter(Boolean);
  return parts.join(' | ');
};

const hasValidDocLink = (item) => {
  return item.docLink && item.docLink !== 'null' && item.docLink.trim() !== '';
};

const hasValidNote = (item) => {
  return item.note && item.note !== 'null' && item.note.trim() !== '';
};

const getPlainTextPreview = (content, maxLength = 200) => {
  if (!content) return '';
  const plain = content
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.length > maxLength ? plain.substring(0, maxLength) + '...' : plain;
};

const truncateText = (text, maxLength = 120) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const openCapacitorSite = async (link) => {
  try {
    if (Haptics && ImpactStyle) {
      await Haptics.impact({ style: ImpactStyle.Light });
    }
    if (Browser) {
      await Browser.open({ url: link });
    } else {
      window.open(link, '_blank');
    }
  } catch (error) {
    window.open(link, '_blank');
  }
};

const handleButtonClick = async (callback) => {
  try {
    if (Haptics && ImpactStyle) {
      await Haptics.impact({ style: ImpactStyle.Light });
    }
  } catch (error) {
    // Haptics not available, continue anyway
  }
  callback();
};

// ============================================
// Report Read Modal
// ============================================

const ReportReadModal = ({ item, isOpen, onClose }) => {
  const { t } = useTranslation('common');

  if (!item) return null;

  const hasSources = item.sources && item.sources.length > 0;

  return (
    <Modal
      id="fullBillModal"
      show={isOpen}
      size="xl"
      centered
      onHide={onClose}
      scrollable
    >
      <Modal.Header id="billModalHeader" closeButton>
        <Modal.Title>
          <Newspaper className="me-2" />
          {item.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="descriptionModalBody">
        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '1rem' }}>
          {item.feedName && <span>{item.feedName}</span>}
          {item.date && <span> • {formatDate(item.date)}</span>}
        </div>

        {item.note && (
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '1.25rem',
            fontSize: '14px',
            color: 'var(--text-secondary)'
          }}>
            <strong style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('common:note', 'Note')}</strong>
            <p style={{ margin: '4px 0 0 0' }}>{item.note}</p>
          </div>
        )}

        <div className="report-markdown-content">
          <ReactMarkdown>{item.content}</ReactMarkdown>
        </div>

        {hasSources && (
          <>
            <h5 className="mt-4 mb-3">
              <List className="me-2" />
              {t('common:sourcesCount', 'Sources ({{count}})', { count: item.sources.length })}
            </h5>
            {item.sources.map((source, index) => (
              <div key={`source-${source.url}-${index}`} className="feed-item mb-3">
                <Card>
                  <Card.Title
                    id="billText"
                    className="card-title p-3"
                    onClick={() => openCapacitorSite(source.url)}
                    style={{ cursor: 'pointer' }}
                  >
                    {truncateText(source.title, 200)}
                  </Card.Title>
                  {source.date && (
                    <Card.Body id="billBodyText">
                      {formatDate(source.date)}
                    </Card.Body>
                  )}
                  <Card.Footer className="d-flex">
                    <Button
                      className="clickable"
                      id="billLink"
                      onClick={() => openCapacitorSite(source.url)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
                    </Button>
                  </Card.Footer>
                </Card>
              </div>
            ))}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ============================================
// Fact Detail Modal
// ============================================

const FactDetailModal = ({ item, isOpen, onClose }) => {
  const { t } = useTranslation('common');

  if (!item) return null;

  // Parse ALL sources — fact-check results store these in allSources
  const allSources = (() => {
    try { return JSON.parse(item.allSources || '[]'); } catch { return []; }
  })();

  // Fall back to single source field for plain facts
  const singleSource = (() => {
    try { return JSON.parse(item.source || '{}'); } catch { return {}; }
  })();

  const tags = (() => {
    try { return JSON.parse(item.tags || '[]'); } catch { return []; }
  })();

  // Decide which sources array to show
  const sources = allSources.length > 0
    ? allSources
    : (singleSource.url ? [singleSource] : []);

  const category = safeDisplay(item.topic) || safeDisplay(item.feedName);

  return (
    <Modal
      id="fullBillModal"
      show={isOpen}
      size="xl"
      centered
      scrollable
      onHide={onClose}
    >
      <Modal.Header id="billModalHeader" closeButton>
        <Modal.Title>
          <ShieldCheck className="me-2" />
          {category}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id="descriptionModalBody">

        {/* Title / claim */}
        <Card.Title id="billText" style={{ marginBottom: '0.75rem' }}>
          {item.title}
        </Card.Title>

        {/* Key figure (verdict + confidence for fact-checks, stat for facts) */}
        {safeDisplay(item.keyFigure) && (
          <div style={{
            fontSize: '1.6rem',
            fontWeight: 800,
            color: 'var(--accent-hover)',
            fontFamily: 'var(--site-font)',
            lineHeight: 1.2,
            marginBottom: '1rem',
          }}>
            {item.keyFigure}
          </div>
        )}

        {/* Summary / fact text */}
        <Card.Body id="billBodyText" style={{ padding: 0, marginBottom: '1rem' }}>
          {item.factText}
        </Card.Body>

        {/* Context (plain facts) */}
        {safeDisplay(item.context) && (
          <div style={{
            borderLeft: '3px solid var(--border-primary)',
            background: 'var(--bg-card)',
            borderRadius: '0 8px 8px 0',
            padding: '12px 14px',
            marginBottom: '1rem',
          }}>
            <div style={{
              fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 6,
            }}>
              {t('common:context', 'Context')}
            </div>
            <Card.Body id="billBodyText" style={{ padding: 0 }}>
              {item.context}
            </Card.Body>
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '1rem' }}>
            {tags.map((tag, i) => (
              <Badge key={i} className="tag-badge" bg="secondary">{tag}</Badge>
            ))}
          </div>
        )}

        {/* Saved note */}
        {hasValidNote(item) && (
          <div style={{
            background: 'var(--bg-card)', borderRadius: '8px',
            padding: '10px 12px', marginBottom: '1rem',
            fontSize: '14px', color: 'var(--text-secondary)'
          }}>
            <strong style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('common:note', 'Note')}</strong>
            <p style={{ margin: '4px 0 0 0' }}>{item.note}</p>
          </div>
        )}

        {/* All sources */}
        {sources.length > 0 && (
          <>
            <div style={{
              fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8,
            }}>
              {t('common:sourcesCount', 'Sources ({{count}})', { count: sources.length })}
            </div>
            {sources.map((src, i) => {
              const srcUrl = src.url || '';
              const srcName = src.name || srcUrl;
              const srcAgency = src.agency || '';
              return (
                <div key={i} className="feed-item mb-3">
                  <Card>
                    <Card.Title
                      id="billText"
                      className="card-title p-3"
                      onClick={() => srcUrl && openCapacitorSite(srcUrl)}
                      style={{ cursor: srcUrl ? 'pointer' : 'default' }}
                    >
                      {srcName}
                    </Card.Title>
                    {srcAgency && (
                      <Card.Body id="billBodyText">{srcAgency}</Card.Body>
                    )}
                    {srcUrl && (
                      <Card.Footer className="d-flex">
                        <Button className="clickable" id="billLink"
                          onClick={() => openCapacitorSite(srcUrl)}>
                          <BoxArrowUpRight className="me-1" /> {t('common:viewSource', 'View Source')}
                        </Button>
                      </Card.Footer>
                    )}
                  </Card>
                </div>
              );
            })}
          </>
        )}

      </Modal.Body>
    </Modal>
  );
};

// ============================================
// Collection Item Component
// ============================================

export const CollectionItem = ({ 
  item, 
  index, 
  onEditNote, 
  onRemove 
}) => {
  const { t } = useTranslation('common');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [factModalOpen, setFactModalOpen] = useState(false);

  if (!item || !item.title) return null;

  const metadataString = buildMetadataString(item);
  const hasDocLink = hasValidDocLink(item);
  const hasNote = hasValidNote(item);
  const formattedDate = formatDate(item.date);
  const description = safeDisplay(item.description);
  const isReport = item.type === 'report';
  const isFact = item.type === 'fact' || (typeof item.factText === 'string' && item.factText.trim() !== '');

  const handleNoteClick = () => {
    handleButtonClick(() => onEditNote(item));
  };

  return (
    <div key={item.id || index} className="collection-item-wrapper">
      <div className="d-flex align-items-center justify-content-center">
        <Container id="feedContainerFeedPage">
          <Card.Title id="billText">{item.title}</Card.Title>
          
          <Card.Body id="billText">
            {metadataString && (
              <div className="metadata-text">{metadataString}</div>
            )}
            
            {description && (
              <div style={{ marginBottom: '4px' }}>{description}</div>
            )}
            
            {formattedDate && (
              <div className="date-text">{formattedDate}</div>
            )}
            
            {hasNote && (
              <div 
                className="note-display"
                onClick={handleNoteClick}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleNoteClick();
                }}
              >
                <strong>{t('common:note', 'Note')}:</strong>
                <p>{item.note}</p>
              </div>
            )}

            {isReport && item.content && (
              <div className="note-display" style={{ marginTop: '0.75rem' }}>
                <strong>{t('common:reportSummary', 'Report Summary')}</strong>
                <p>{getPlainTextPreview(item.content)}</p>
                {item.sources && item.sources.length > 0 && (
                  <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    {t('common:sourcesPlural', '{{count}} source', { count: item.sources.length })}
                  </p>
                )}
              </div>
            )}

            {isFact && (
              <div className="note-display" style={{ marginTop: '0.75rem' }}>
                <strong>{t('common:fact', 'Fact')}</strong>
                {safeDisplay(item.keyFigure) && (
                  <p style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    color: 'var(--accent-hover)',
                    margin: '4px 0 2px',
                  }}>
                    {item.keyFigure}
                  </p>
                )}
                <p>{truncateText(item.factText, 160)}</p>
              </div>
            )}
            
            {(() => {
              let parsedTags = [];
              try {
                parsedTags = Array.isArray(item.tags)
                  ? item.tags
                  : JSON.parse(item.tags || '[]');
              } catch { parsedTags = []; }
              return parsedTags.length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {parsedTags.map(tag => (
                    <Badge key={tag} className="tag-badge" bg="secondary">{tag}</Badge>
                  ))}
                </div>
              ) : null;
            })()}
          </Card.Body>
          
          <div className="button-container">
            {isReport && item.content && (
              <Button
                id="billLink"
                onClick={() => handleButtonClick(() => setReportModalOpen(true))}
              >
                <FileText /> {t('common:read', 'Read')}
              </Button>
            )}

            {isFact && (
              <Button
                id="billLink"
                onClick={() => handleButtonClick(() => setFactModalOpen(true))}
              >
                <ShieldCheck className="me-1" /> {t('common:viewFact', 'View Fact')}
              </Button>
            )}

            {!isReport && !isFact && item.url && (
              <Button 
                id="billLink" 
                onClick={(e) => {
                  e.preventDefault();
                  openCapacitorSite(item.url);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
              </Button>
            )}
            
            {hasDocLink && (
              <Button 
                id="billLink" 
                onClick={(e) => {
                  e.preventDefault();
                  openCapacitorSite(item.docLink);
                }}
              >
                <FileText /> {t('common:document', 'Document')}
              </Button>
            )}
            
            <Button 
              id="billLink" 
              onClick={() => handleButtonClick(() => onEditNote(item))}
              title={t('common:editNoteTooltip', 'Edit note')}
            >
              <Pencil />
            </Button>
            
            <Button 
              id="billLink" 
              onClick={() => handleButtonClick(() => onRemove(item.id))}
              title={t('common:removeFromCollection', 'Remove from collection')}
            >
              <X />
            </Button>
          </div>
        </Container>
      </div>

      <ReportReadModal
        item={item}
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
      />

      <FactDetailModal
        item={item}
        isOpen={factModalOpen}
        onClose={() => setFactModalOpen(false)}
      />
    </div>
  );
};

export default CollectionItem;